import { type FC, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import { Button } from '@kizen/kds/Button';
import {
  ExecutionHistoryContext,
  ExecutionToShow,
  MAX_DEBUGGERS_TO_START,
} from 'ts-components/AutomationEngine/ExecutionHistory/context';
import {
  postAutomationExecutionDebugStep,
  postAutomationExecutionPlay,
  postAutomationExecutionSkipAndResume,
} from '@kizen/api/automation';
import {
  Automation,
  ExecutionHistory,
  Step,
} from 'pages/AutomationEngine/types';
import {
  useAutomationsDispatch,
  useAutomationsSelector,
} from '__pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';
import { getAutomationId } from 'pages/AutomationEngine/store/selectors';
import { toastVariant } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { PathSelectButton } from '../PathSelectButton';
import type { ActionType, ContinueWithBranch } from '../types';
import { getDisplayNames } from '@kizen/api/records';
import { StyledControlWrapper } from './styles';

type DebuggerControlsProps = {
  step: Step;
  selectedHistory: ExecutionHistory;
};

type AutomationAndExecutions = Record<string, string[]>;

const prepDebuggers = (executionsStarted: AutomationAndExecutions) => {
  let started = 0;

  let executionsToShow: ExecutionToShow[] = [];

  // get the first 10 we are going to open in new windows
  for (const [automationId, entities] of Object.entries(executionsStarted)) {
    const remaining = MAX_DEBUGGERS_TO_START - started;

    executionsToShow = [
      ...executionsToShow,
      ...entities
        .slice(0, remaining)
        .map((executionId) => ({ automationId, executionId })),
    ];

    started += entities.length;

    if (started >= MAX_DEBUGGERS_TO_START) {
      break;
    }
  }

  return executionsToShow;
};

const fireOffDebuggers = (executionsToShow: ExecutionToShow[]) => {
  if (executionsToShow.length) {
    executionsToShow.forEach(({ automationId, executionId }) => {
      window.open(
        `/agentic-workflow/${automationId}?history=${executionId}`,
        '_blank',
        'noopener,noreferrer'
      );
    });
  }

  return executionsToShow;
};

export const DebuggerControls = ({
  step,
  selectedHistory,
}: DebuggerControlsProps) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();
  const automationId: Automation['id'] =
    useAutomationsSelector(getAutomationId);

  const {
    historyId,
    runningDebugAction,
    setRunningDebugAction,
    setDebugWindowsToOpen,
    setShowDebugToStartModal,
    setDebugExecutionsList,
    setDebugAutomationsList,
    selectedHistoryStep,
    setSelectedHistoryStep,
  } = useContext(ExecutionHistoryContext);

  const runLabel =
    selectedHistory.status === 'paused_by_failure'
      ? t('Retry')
      : step.type === 'delay'
        ? t('Proceed')
        : t('Run');

  const runColor =
    selectedHistory.status === 'paused_by_failure' ? 'warning' : 'primary';

  const runIcon = step.type === 'delay' ? 'custom-forward' : 'custom-play';

  const showDebugButton = step.type === 'start_automation';

  const isHistoryStepSelected = selectedHistoryStep?.id === selectedHistory.id;

  const handleDebugPathSubmit = useCallback(
    async (continue_with_branch: ContinueWithBranch) => {
      try {
        if (!selectedHistory.id) {
          dispatch(
            actions.toast({
              variant: toastVariant.FAILURE,
              message: t('The debug skip action failed.'),
            })
          );
          return;
        }

        if (!isHistoryStepSelected) {
          setSelectedHistoryStep(selectedHistory);
          dispatch(
            actions.setFocusStep({
              step: selectedHistory.stepId || selectedHistory.triggerId,
            })
          );
        }

        await postAutomationExecutionDebugStep(AxiosService, historyId!, {
          action: 'skip',
          history_id: selectedHistory.id,
          continue_with_branch,
        });

        dispatch(
          actions.loadExecutionHistory({
            executionId: historyId,
            automationId,
          })
        );
      } catch (error) {
        const message = getErrorMessage(getOriginalError(error));
        dispatch(
          actions.toast({
            variant: toastVariant.FAILURE,
            message: message || t('The debug Skip action failed.'),
          })
        );
      }
    },
    [
      automationId,
      dispatch,
      historyId,
      selectedHistory,
      isHistoryStepSelected,
      setSelectedHistoryStep,
      t,
    ]
  );

  const handleDebugClick = useCallback(
    async (action: ActionType) => {
      try {
        if (!selectedHistory.id) {
          dispatch(
            actions.toast({
              variant: toastVariant.FAILURE,
              message: t('The debug {{action}} action failed.', {
                action: action === 'execute' ? runLabel : t('Skip'),
              }),
            })
          );
          return;
        }
        setRunningDebugAction(selectedHistory.id);

        if (!isHistoryStepSelected) {
          setSelectedHistoryStep(selectedHistory);
          dispatch(
            actions.setFocusStep({
              step: selectedHistory.stepId || selectedHistory.triggerId,
            })
          );
        }

        const executionDetails = await postAutomationExecutionDebugStep(
          AxiosService,
          historyId!,
          {
            action,
            history_id: selectedHistory.id,
          }
        );

        if (action === 'debug') {
          const startedExecutions = Object.values(
            executionDetails.started_executions || {}
          ).reduce((prev, executions) => {
            prev += executions.length;
            return prev;
          }, 0);

          const restartedExecutions = Object.values(
            executionDetails.restarted_executions || {}
          ).reduce((prev, executions) => {
            prev += executions.length;

            return prev;
          }, 0);

          const totalExecutions = startedExecutions + restartedExecutions;

          const mergedExecutions = {
            ...(executionDetails.started_executions || {}),
            ...(executionDetails.restarted_executions || {}),
          };

          const executionsToStart = prepDebuggers(mergedExecutions);

          if (totalExecutions === 0) {
            dispatch(
              actions.toast({
                variant: toastVariant.SUCCESS,
                message: t('No Executions were started.'),
              })
            );
          } else if (totalExecutions === 1) {
            dispatch(
              actions.toast({
                variant: toastVariant.SUCCESS,
                message: t('One Execution was started.'),
              })
            );
            fireOffDebuggers(executionsToStart);
          } else if (totalExecutions > 1) {
            const displayNames = await getDisplayNames(AxiosService, {
              automation_ids: Object.keys(mergedExecutions),
            });
            let automationList;
            if (displayNames?.results?.automations?.length) {
              automationList = displayNames?.results?.automations.map(
                ({ id, display_name }) => {
                  return {
                    automationId: id,
                    name: display_name,
                    count: mergedExecutions[id]?.length || 0,
                  };
                }
              );
            } else {
              automationList = Object.entries(mergedExecutions).map(
                ([id, executions]) => {
                  return {
                    automationId: id,
                    name: id,
                    count: executions?.length,
                  };
                }
              );
            }

            setDebugAutomationsList(automationList);
            setDebugExecutionsList(executionsToStart);
            setShowDebugToStartModal(true);
            setDebugWindowsToOpen(totalExecutions);
          }
        }
        dispatch(
          actions.loadExecutionHistory({
            executionId: historyId,
            automationId,
          })
        );
      } catch (error) {
        const message = getErrorMessage(getOriginalError(error));
        dispatch(
          actions.toast({
            variant: toastVariant.FAILURE,
            message:
              message ||
              t('The debug {{action}} action failed.', {
                action: action === 'execute' ? runLabel : t('Skip'),
              }),
          })
        );
      }
    },
    [
      automationId,
      dispatch,
      historyId,
      runLabel,
      setDebugAutomationsList,
      setDebugExecutionsList,
      setDebugWindowsToOpen,
      setRunningDebugAction,
      setShowDebugToStartModal,
      isHistoryStepSelected,
      setSelectedHistoryStep,
      selectedHistory,
      t,
    ]
  );

  return (
    <StyledControlWrapper data-qa="debug-controls">
      <div>
        {step.type === 'goal' || step.type === 'condition' ? (
          <PathSelectButton
            disabled={!!runningDebugAction}
            onClick={handleDebugPathSubmit}
            isGoal={step.type === 'goal'}
          >
            {t('Skip')}
          </PathSelectButton>
        ) : (
          <Button
            leftIcon="text-redo"
            variant="text"
            qa={{
              action: 'action-skip',
              type: 'icon-button',
            }}
            size="small"
            color="secondary"
            onClick={() => handleDebugClick('skip')}
            disabled={!!runningDebugAction}
          >
            {t('Skip')}
          </Button>
        )}
      </div>
      <div>
        {showDebugButton ? (
          <Button
            leftIcon="action-smartconnector-dry-run"
            variant="text"
            qa={{
              action: 'action-debug',
              type: 'icon-button',
            }}
            size="small"
            color={runColor}
            onClick={() => handleDebugClick('debug')}
            disabled={!!runningDebugAction}
          >
            Debug
          </Button>
        ) : null}
        {step.type !== 'goal' ? (
          <Button
            leftIcon={runIcon}
            variant="text"
            qa={{
              action: 'action-run',
              type: 'icon-button',
            }}
            size="small"
            color={runColor}
            onClick={() => handleDebugClick('execute')}
            disabled={!!runningDebugAction}
          >
            {runLabel}
          </Button>
        ) : null}
      </div>
    </StyledControlWrapper>
  );
};

export const FailedStepControls: FC<{ step: Step }> = ({ step }) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();
  const automationId: string = useAutomationsSelector(getAutomationId);

  const { historyId } = useContext(ExecutionHistoryContext);

  const [running, setRunning] = useState(false);

  const handlePathSubmit = useCallback(
    async (continue_with_branch: ContinueWithBranch) => {
      try {
        setRunning(true);
        await postAutomationExecutionSkipAndResume(AxiosService, historyId!, {
          skip_step_id: step.id,
          continue_with_branch,
        });

        dispatch(
          actions.loadExecutionHistory({
            executionId: historyId,
            automationId,
          })
        );
      } catch (error) {
        const message = getErrorMessage(getOriginalError(error));
        dispatch(
          actions.toast({
            variant: toastVariant.FAILURE,
            message: message || t('The debug Skip action failed.'),
          })
        );
      } finally {
        setRunning(false);
      }
    },
    [automationId, dispatch, historyId, step.id, t]
  );

  const handleClick = useCallback(
    async (action: ActionType) => {
      try {
        setRunning(true);

        if (action === 'execute') {
          await postAutomationExecutionPlay(AxiosService, historyId!);
        } else {
          await postAutomationExecutionSkipAndResume(AxiosService, historyId!, {
            skip_step_id: step.id,
          });
        }

        dispatch(
          actions.loadExecutionHistory({
            executionId: historyId,
            automationId,
          })
        );
      } catch (error) {
        const message = getErrorMessage(getOriginalError(error));
        dispatch(
          actions.toast({
            variant: toastVariant.FAILURE,
            message:
              message ||
              t('{{action}} action failed.', {
                action: action === 'execute' ? t('Retry') : t('Skip'),
              }),
          })
        );
      } finally {
        setRunning(false);
      }
    },
    [automationId, dispatch, historyId, step.id, t]
  );

  return (
    <StyledControlWrapper data-qa="debug-controls">
      <div>
        {step.type === 'goal' || step.type === 'condition' ? (
          <PathSelectButton
            disabled={!!running}
            onClick={handlePathSubmit}
            isGoal={step.type === 'goal'}
          >
            {t('Skip')}
          </PathSelectButton>
        ) : (
          <Button
            leftIcon="text-redo"
            variant="text"
            qa={{
              action: 'action-skip',
              type: 'icon-button',
            }}
            size="small"
            color="secondary"
            onClick={() => handleClick('skip')}
            disabled={!!running}
          >
            {t('Skip')}
          </Button>
        )}
      </div>
      <div>
        <Button
          leftIcon="custom-play"
          variant="text"
          qa={{
            action: 'action-run',
            type: 'icon-button',
          }}
          size="small"
          color="warning"
          onClick={() => handleClick('execute')}
          disabled={!!running}
        >
          {t('Retry')}
        </Button>
      </div>
    </StyledControlWrapper>
  );
};
